import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";

interface SchemaFormRendererProps {
  schema: Record<string, any>;
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  errors?: Record<string, string>;
  path?: string;
}

export function SchemaFormRenderer({ schema, values, onChange, errors = {}, path = "" }: SchemaFormRendererProps) {
  if (!schema || schema.type !== "object" || !schema.properties) {
    return <p className="text-sm text-muted-foreground">No configurable properties in this schema.</p>;
  }

  const requiredFields: string[] = schema.required || [];

  const handleChange = (key: string, value: any) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="space-y-4">
      {Object.entries(schema.properties as Record<string, any>).map(([key, prop]: [string, any]) => {
        const fieldPath = path ? `${path}.${key}` : key;
        const isRequired = requiredFields.includes(key);
        const error = errors[fieldPath];
        const label = prop.title || key;
        const description = prop.description;
        const currentValue = values[key];

        return (
          <div key={key} className="space-y-1.5">
            {renderField(key, prop, label, description, isRequired, currentValue, (v) => handleChange(key, v), error, fieldPath, errors, onChange, values)}
          </div>
        );
      })}
    </div>
  );
}

function renderField(
  key: string,
  prop: any,
  label: string,
  description: string | undefined,
  isRequired: boolean,
  value: any,
  onFieldChange: (v: any) => void,
  error: string | undefined,
  fieldPath: string,
  errors: Record<string, string>,
  onParentChange: (values: Record<string, any>) => void,
  parentValues: Record<string, any>
): React.ReactNode {
  // Enum → select
  if (prop.enum) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">
          {label} {isRequired && <span className="text-destructive">*</span>}
        </Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <Select value={value?.toString() || ""} onValueChange={onFieldChange}>
          <SelectTrigger><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
          <SelectContent>
            {(prop.enum as any[]).map((opt: any) => (
              <SelectItem key={String(opt)} value={String(opt)}>{String(opt)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  switch (prop.type) {
    case "boolean":
      return (
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium">{label}</Label>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
          <Switch checked={!!value} onCheckedChange={onFieldChange} />
        </div>
      );

    case "integer":
    case "number":
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            {label} {isRequired && <span className="text-destructive">*</span>}
          </Label>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
          <Input
            type="number"
            value={value ?? ""}
            onChange={(e) => onFieldChange(e.target.value === "" ? undefined : Number(e.target.value))}
            min={prop.minimum}
            max={prop.maximum}
            placeholder={prop.default !== undefined ? `Default: ${prop.default}` : undefined}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      );

    case "object":
      return (
        <NestedObjectField
          fieldKey={key}
          prop={prop}
          label={label}
          description={description}
          value={value || {}}
          onChange={onFieldChange}
          errors={errors}
          fieldPath={fieldPath}
        />
      );

    case "array":
      return (
        <ArrayField
          fieldKey={key}
          prop={prop}
          label={label}
          description={description}
          isRequired={isRequired}
          value={value || []}
          onChange={onFieldChange}
          errors={errors}
          fieldPath={fieldPath}
        />
      );

    case "string":
    default:
      // Long text
      if (prop.format === "text" || (prop.maxLength && prop.maxLength > 200)) {
        return (
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              {label} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            <Textarea
              value={value || ""}
              onChange={(e) => onFieldChange(e.target.value)}
              placeholder={prop.default !== undefined ? `Default: ${prop.default}` : undefined}
              maxLength={prop.maxLength}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        );
      }

      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            {label} {isRequired && <span className="text-destructive">*</span>}
          </Label>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
          <Input
            value={value || ""}
            onChange={(e) => onFieldChange(e.target.value)}
            placeholder={prop.default !== undefined ? `Default: ${prop.default}` : undefined}
            maxLength={prop.maxLength}
            minLength={prop.minLength}
            pattern={prop.pattern}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      );
  }
}

function NestedObjectField({
  fieldKey, prop, label, description, value, onChange, errors, fieldPath,
}: {
  fieldKey: string; prop: any; label: string; description?: string;
  value: Record<string, any>; onChange: (v: any) => void;
  errors: Record<string, string>; fieldPath: string;
}) {
  const [open, setOpen] = React.useState(false);

  if (!prop.properties) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">{label}</Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <Textarea
          value={typeof value === "object" ? JSON.stringify(value, null, 2) : value || ""}
          onChange={(e) => {
            try { onChange(JSON.parse(e.target.value)); } catch { /* keep raw */ }
          }}
          placeholder="Enter JSON object"
          className="font-mono text-xs"
          rows={4}
        />
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-3 h-auto border rounded-lg">
          <div className="text-left">
            <span className="text-sm font-medium">{label}</span>
            {description && <p className="text-xs text-muted-foreground font-normal">{description}</p>}
          </div>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-4 pt-2 border-l-2 border-muted ml-3">
        <SchemaFormRenderer
          schema={prop}
          values={value}
          onChange={onChange}
          errors={errors}
          path={fieldPath}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

function ArrayField({
  fieldKey, prop, label, description, isRequired, value, onChange, errors, fieldPath,
}: {
  fieldKey: string; prop: any; label: string; description?: string; isRequired: boolean;
  value: any[]; onChange: (v: any[]) => void;
  errors: Record<string, string>; fieldPath: string;
}) {
  const itemSchema = prop.items;

  const addItem = () => {
    if (!itemSchema) return;
    const defaultItem = itemSchema.type === "object" ? {} : itemSchema.type === "boolean" ? false : "";
    onChange([...value, defaultItem]);
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, newValue: any) => {
    const updated = [...value];
    updated[index] = newValue;
    onChange(updated);
  };

  // Simple string/number arrays
  if (itemSchema && (itemSchema.type === "string" || itemSchema.type === "number" || itemSchema.type === "integer")) {
    // If items have enum, use multi-select style
    if (itemSchema.enum) {
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            {label} {isRequired && <span className="text-destructive">*</span>}
          </Label>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
          <div className="flex flex-wrap gap-2">
            {(itemSchema.enum as any[]).map((opt: any) => {
              const selected = value.includes(opt);
              return (
                <Button
                  key={String(opt)}
                  type="button"
                  variant={selected ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (selected) onChange(value.filter((v) => v !== opt));
                    else onChange([...value, opt]);
                  }}
                >
                  {String(opt)}
                </Button>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">
          {label} {isRequired && <span className="text-destructive">*</span>}
        </Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <div className="space-y-2">
          {value.map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item || ""}
                onChange={(e) => updateItem(index, itemSchema.type === "number" ? Number(e.target.value) : e.target.value)}
                type={itemSchema.type === "number" || itemSchema.type === "integer" ? "number" : "text"}
              />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            <Plus className="h-4 w-4 mr-1" /> Add item
          </Button>
        </div>
      </div>
    );
  }

  // Object arrays
  if (itemSchema && itemSchema.type === "object") {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">
          {label} {isRequired && <span className="text-destructive">*</span>}
        </Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <div className="space-y-3">
          {value.map((item, index) => (
            <div key={index} className="border rounded-lg p-3 relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 h-6 w-6"
                onClick={() => removeItem(index)}
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
              <p className="text-xs font-medium text-muted-foreground mb-2">Item {index + 1}</p>
              <SchemaFormRenderer
                schema={itemSchema}
                values={item || {}}
                onChange={(v) => updateItem(index, v)}
                errors={errors}
                path={`${fieldPath}[${index}]`}
              />
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            <Plus className="h-4 w-4 mr-1" /> Add {itemSchema.title || "item"}
          </Button>
        </div>
      </div>
    );
  }

  // Fallback: JSON textarea
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <Textarea
        value={JSON.stringify(value, null, 2)}
        onChange={(e) => {
          try { onChange(JSON.parse(e.target.value)); } catch { /* keep raw */ }
        }}
        className="font-mono text-xs"
        rows={4}
      />
    </div>
  );
}
